import { createClient } from '@supabase/supabase-js';
import { newId } from './ids';

// Fallback a placeholders para que createClient NO lance al importar (eso dejaba
// la pantalla en blanco). Si faltan, se loguea claro y las llamadas fallarán
// visiblemente (por toast), pero la app igual renderiza.
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'placeholder-key';

if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
  console.error('❌ Falta REACT_APP_SUPABASE_URL o REACT_APP_SUPABASE_ANON_KEY en .env.local — la app no podrá conectarse a la base.');
}

export const supabase = createClient(supabaseUrl, supabaseKey);

// ─── PERFILES USUARIO ──────────────────────────────────
export const perfilService = {
  async obtener() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from("perfil_usuario")
      .select("*")
      .eq("id", user.id)
      .single();

    return data || null;
  },

  async crear(perfil) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("No hay usuario autenticado");

    // upsert: crea la fila o la actualiza si ya existía (registro a medias)
    const { data, error } = await supabase
      .from("perfil_usuario")
      .upsert([{ id: user.id, ...perfil }], { onConflict: "id" })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async actualizar(updates) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("No hay usuario");

    const { data, error } = await supabase
      .from("perfil_usuario")
      .update(updates)
      .eq("id", user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async listarTodos() {
    const { data, error } = await supabase
      .from("perfil_usuario")
      .select("id, nombre_campo, email, rol, features");

    if (error) throw error;
    return data || [];
  },

  async actualizarPerfil(userId, updates) {
    const { data, error } = await supabase
      .from("perfil_usuario")
      .update(updates)
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};

// ─── PRECIOS ACG (Edge Function) ──────────────────────
export const preciosService = {
  async obtener() {
    const { data, error } = await supabase.functions.invoke("precios-acg");
    if (error) {
      console.warn("No se pudieron obtener precios ACG:", error.message);
      return null;
    }
    return data; // { fecha, fuente, live, items:[...] }
  },
};

// ─── ASESOR IA (Edge Function) ────────────────────────
export const iaService = {
  async generar({ resumen, totalAnim, nPotreros }) {
    const { data, error } = await supabase.functions.invoke("asesor-ia", {
      body: { resumen, totalAnim, nPotreros },
    });
    if (error) throw new Error(error.message || "No se pudo contactar al asesor");
    if (data?.error) throw new Error(data.error);
    return data?.text || "Sin respuesta.";
  },
};

// ─── PRECIOS SEMILLAS ANAPROSE (Edge Function) ────────
export const semillasService = {
  async obtener() {
    const { data, error } = await supabase.functions.invoke("precios-semillas");
    if (error) {
      console.warn("No se pudieron obtener precios de semillas:", error.message);
      return null;
    }
    return data;
  },
};

// ─── MERCADO: PUBLICACIONES (tabla compartida) ────────
export const publicacionService = {
  // Todos los avisos activos de todos los usuarios
  async mercado() {
    const { data, error } = await supabase
      .from("publicaciones").select("*")
      .eq("estado", "activa").order("created_at", { ascending: false });
    if (error) { console.error("Error mercado:", error.message); return []; }
    return data || [];
  },
  // Mis avisos (activos y vendidos)
  async mios(usuarioId) {
    if (!usuarioId) return [];
    const { data, error } = await supabase
      .from("publicaciones").select("*")
      .eq("user_id", usuarioId).order("created_at", { ascending: false });
    if (error) { console.error(error.message); return []; }
    return data || [];
  },
  async crear(usuarioId, pub) {
    if (!usuarioId) throw new Error("usuarioId es requerido");
    const { data, error } = await supabase
      .from("publicaciones").insert([{ ...pub, user_id: usuarioId }]).select().single();
    if (error) throw error;
    return data;
  },
  async actualizar(id, updates) {
    const { error } = await supabase.from("publicaciones").update(updates).eq("id", id);
    if (error) throw error;
  },
  async eliminar(id) {
    const { error } = await supabase.from("publicaciones").delete().eq("id", id);
    if (error) throw error;
  },
};

// ─── MERCADO: CONTACTOS / INTERESADOS ─────────────────
export const contactoService = {
  async enviar(usuarioId, c) {
    if (!usuarioId) throw new Error("usuarioId es requerido");
    const { error } = await supabase.from("contactos").insert([{ ...c, de_user_id: usuarioId }]);
    if (error) throw error;
  },
  // Contactos recibidos en mis avisos
  async recibidos(usuarioId) {
    if (!usuarioId) return [];
    const { data, error } = await supabase
      .from("contactos").select("*")
      .eq("para_user_id", usuarioId).order("created_at", { ascending: false });
    if (error) { console.error(error.message); return []; }
    return data || [];
  },
  async marcarLeido(id) {
    const { error } = await supabase.from("contactos").update({ leido: true }).eq("id", id);
    if (error) throw error;
  },
};

// ─── ESTADO COMPLETO (documento por usuario) ──────────
// Guarda TODO el estado de la app como un único documento JSON por usuario.
// Es el modelo que mejor calza con cómo funciona la app (estado en memoria,
// animales anidados en potreros) y evita errores de mapeo de columnas.
export const estadoService = {
  async cargar(usuarioId, campoId = null) {
    if (!usuarioId) throw new Error("usuarioId es requerido");
    const cId = campoId || usuarioId;

    const { data, error } = await supabase
      .from("campos_metadata")
      .select("*")
      .eq("user_id", usuarioId)
      .eq("campo_id", cId)
      .maybeSingle();

    if (error) {
      console.error("Error cargando campos_metadata:", error);
      return null;
    }
    // Retorna solo metadata, no el blob
    return data ? { nombreCampo: data.nombre_campo, ubicacion: data.ubicacion, features: data.features } : null;
  },

  async guardar(metadata, usuarioId, campoId = null) {
    if (!usuarioId) throw new Error("usuarioId es requerido");
    const cId = campoId || usuarioId;

    // Solo guarda: nombre_campo, ubicacion, features
    const { error } = await supabase
      .from("campos_metadata")
      .upsert(
        [{
          user_id: usuarioId,
          campo_id: cId,
          nombre_campo: metadata.nombreCampo,
          ubicacion: metadata.ubicacion,
          features: metadata.features || {},
          updated_at: new Date().toISOString()
        }],
        { onConflict: "user_id,campo_id" }
      );

    if (error) {
      console.error("Error guardando campos_metadata:", error);
      throw error;
    }
  },

  // Lista todos los campos del usuario (para el selector multi-campo)
  async listarCampos(usuarioId) {
    if (!usuarioId) return [];
    const { data, error } = await supabase
      .from("campos_metadata")
      .select("campo_id, nombre_campo, ubicacion")
      .eq("user_id", usuarioId)
      .order("nombre_campo", { ascending: true });
    if (error) {
      console.error("Error listando campos:", error);
      return [];
    }
    return data || [];
  },
};

// ─── POTREROS ─────────────────────────────────────────
export const potreroService = {
  async getAll(usuarioId, campoId) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('potreros')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching potreros:', error);
      return [];
    }
    return data || [];
  },

  async create(usuarioId, campoId, potrero) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('potreros')
      .insert([{
        id: newId(),
        ...potrero,
        user_id: usuarioId,
        campo_id: campoId,
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(potreroId, updates) {
    const { data, error } = await supabase
      .from('potreros')
      .update(updates)
      .eq('id', potreroId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(potreroId) {
    const { error } = await supabase
      .from('potreros')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', potreroId);

    if (error) throw error;
  },
};

// ─── ANIMALES ──────────────────────────────────────────
export const animalService = {
  async getByPotrero(usuarioId, campoId, potreroId) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('animales')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .eq('potrero_id', potreroId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching animales:', error);
      return [];
    }
    return data || [];
  },

  // Animales vendidos (para la solapa "Vendidos"); persisten con estado='vendido'
  async getVendidos(usuarioId, campoId) {
    if (!usuarioId || !campoId) return [];
    const { data, error } = await supabase
      .from('animales')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .eq('estado', 'vendido')
      .order('fecha_venta', { ascending: false });

    if (error) {
      console.error('Error fetching vendidos:', error);
      return [];
    }
    return data || [];
  },

  async create(usuarioId, campoId, animal) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('animales')
      .insert([{
        id: newId(),
        ...animal,
        user_id: usuarioId,
        campo_id: campoId,
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(animalId, updates) {
    const { data, error } = await supabase
      .from('animales')
      .update(updates)
      .eq('id', animalId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(animalId) {
    const { error } = await supabase
      .from('animales')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', animalId);

    if (error) throw error;
  },

  // Sub-colecciones de TODOS los animales del campo, agrupadas por animal_id.
  // Son 4 queries fijas (no una por animal), así el rodeo se hidrata de una.
  // Sin esto los animales vuelven de la BD "planos" y la UI lee 0 en peso,
  // ganancia, carga animal (UG/ha) y tratamientos.
  async getDetallesPorCampo(usuarioId, campoId) {
    const vacio = { pesadas: {}, eventos: {}, sanidad: {}, movimientos: {} };
    if (!usuarioId || !campoId) return vacio;

    const [pesadas, eventos, sanidad, movimientos, potreros] = await Promise.all([
      pesadaService.getByCampo(usuarioId, campoId),
      eventoReproduccionService.getByCampo(usuarioId, campoId),
      tratamientoService.getByCampo(usuarioId, campoId),
      movimientoService.getByCampo(usuarioId, campoId),
      potreroService.getAll(usuarioId, campoId).catch(() => []),
    ]);

    // El historial de movimientos se muestra como "origen → destino" por nombre,
    // pero la tabla guarda solo los ids: los resolvemos acá para que el nombre
    // esté disponible venga el movimiento de memoria o de la BD.
    const nombrePorId = new Map((potreros || []).map(p => [p.id, p.nombre]));
    const movsConNombre = (movimientos || []).map(m => ({
      ...m,
      desdeNombre: nombrePorId.get(m.desdeId) || "",
      hastaNombre: nombrePorId.get(m.hastaId) || "",
    }));

    const agrupar = (filas) => (filas || []).reduce((acc, f) => {
      if (!f?.animal_id) return acc;
      (acc[f.animal_id] = acc[f.animal_id] || []).push(f);
      return acc;
    }, {});

    return {
      pesadas: agrupar(pesadas),
      eventos: agrupar(eventos),
      sanidad: agrupar(sanidad),
      movimientos: agrupar(movsConNombre),
    };
  },
};

// ─── STOCK SANIDAD ────────────────────────────────────
// Mapea fila BD (snake_case) ↔ objeto app (camelCase) para control_sanitario
const normStock = (r) => (r ? { ...r, stockMin: r.stock_min } : r);
const denormStock = (item) => {
  const { stockMin, ...rest } = item;
  return stockMin !== undefined ? { ...rest, stock_min: stockMin } : rest;
};

export const stockService = {
  async getAll(usuarioId, campoId) {
    if (!usuarioId || !campoId) return [];

    const { data, error } = await supabase
      .from('control_sanitario')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching stock:', error);
      return [];
    }
    return (data || []).map(normStock);
  },

  async create(usuarioId, campoId, item) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('control_sanitario')
      .insert([{ id: newId(), ...denormStock(item), user_id: usuarioId, campo_id: campoId }])
      .select()
      .single();

    if (error) throw error;
    return normStock(data);
  },

  async update(id, updates) {
    const { data, error } = await supabase
      .from('control_sanitario')
      .update(denormStock(updates))
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return normStock(data);
  },

  async delete(id) {
    const { error } = await supabase
      .from('control_sanitario')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── TRANSACCIONES ────────────────────────────────────
export const transaccionService = {
  async getAll(usuarioId, campoId) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('transacciones')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error fetching transacciones:', error);
      return [];
    }
    return data || [];
  },

  async create(usuarioId, campoId, transaccion) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('transacciones')
      .insert([{
        id: newId(),
        ...transaccion,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(transaccionId, updates) {
    const { data, error } = await supabase
      .from('transacciones')
      .update(updates)
      .eq('id', transaccionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('transacciones')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── FARDOS ───────────────────────────────────────────
export const fardoService = {
  async getAll(usuarioId, campoId) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('fardos')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error fetching fardos:', error);
      return [];
    }
    return data || [];
  },

  async create(usuarioId, campoId, fardo) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('fardos')
      .insert([{
        id: newId(),
        ...fardo,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      .from('fardos')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('fardos')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── ACTIVIDADES (actividades de potrero) ────────────
export const actividadService = {
  async getByPotrero(potreroId) {
    if (!potreroId) throw new Error("potreroId es requerido");

    const { data, error } = await supabase
      .from('actividades')
      .select('*')
      .eq('potrero_id', potreroId)
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error fetching actividades:', error);
      return [];
    }
    return data || [];
  },

  async create(usuarioId, campoId, potreroId, actividad) {
    if (!usuarioId || !campoId || !potreroId) throw new Error("usuarioId, campoId, potreroId son requeridos");

    const { data, error } = await supabase
      .from('actividades')
      .insert([{
        id: newId(),
        ...actividad,
        potrero_id: potreroId,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(actividadId, updates) {
    const { data, error } = await supabase
      .from('actividades')
      .update(updates)
      .eq('id', actividadId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(actividadId) {
    const { error } = await supabase
      .from('actividades')
      .delete()
      .eq('id', actividadId);

    if (error) throw error;
  },
};

// ─── PESADAS (pesajes de animales) ─────────────────
export const pesadaService = {
  async getByAnimal(animalId) {
    if (!animalId) throw new Error("animalId es requerido");

    const { data, error } = await supabase
      .from('pesadas')
      .select('*')
      .eq('animal_id', animalId)
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error fetching pesadas:', error);
      return [];
    }
    return data || [];
  },

  // Todas las pesadas del campo (join con animales) para análisis de ganancia de peso
  async getByCampo(usuarioId, campoId) {
    if (!usuarioId || !campoId) return [];
    const { data, error } = await supabase
      .from('pesadas')
      .select('id, fecha, kg, obs, animal_id, animales!inner(caravana, categoria, especie, campo_id, user_id)')
      .eq('animales.user_id', usuarioId)
      .eq('animales.campo_id', campoId)
      .order('fecha', { ascending: true });
    if (error) { console.error('Error pesadas campo:', error); return []; }
    return (data || []).map(p => ({ id: p.id, fecha: p.fecha, kg: p.kg, obs: p.obs, animal_id: p.animal_id,
      caravana: p.animales?.caravana, categoria: p.animales?.categoria, especie: p.animales?.especie }));
  },

  async create(usuarioId, animalId, pesada) {
    if (!usuarioId || !animalId) throw new Error("usuarioId, animalId son requeridos");

    const { data, error } = await supabase
      .from('pesadas')
      .insert([{
        id: newId(),
        ...pesada,
        animal_id: animalId,
        usuario_id: usuarioId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(pesadaId) {
    const { error } = await supabase
      .from('pesadas')
      .delete()
      .eq('id', pesadaId);

    if (error) throw error;
  },
};

// ─── EVENTOS_REPRODUCCION ────────────────────────────
export const eventoReproduccionService = {
  async getByAnimal(animalId) {
    if (!animalId) throw new Error("animalId es requerido");

    const { data, error } = await supabase
      .from('eventos_reproduccion')
      .select('*')
      .eq('animal_id', animalId)
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error fetching eventos_reproduccion:', error);
      return [];
    }
    return data || [];
  },

  // Todos los eventos reproductivos del campo (join con animales)
  async getByCampo(usuarioId, campoId) {
    if (!usuarioId || !campoId) return [];
    const { data, error } = await supabase
      .from('eventos_reproduccion')
      .select('id, tipo, fecha, obs, animal_id, animales!inner(caravana, categoria, campo_id, user_id)')
      .eq('animales.user_id', usuarioId)
      .eq('animales.campo_id', campoId)
      .order('fecha', { ascending: false });
    if (error) { console.error('Error eventos campo:', error); return []; }
    return (data || []).map(e => ({ id: e.id, tipo: e.tipo, fecha: e.fecha, obs: e.obs, animal_id: e.animal_id,
      caravana: e.animales?.caravana, categoria: e.animales?.categoria }));
  },

  async create(usuarioId, animalId, evento) {
    if (!usuarioId || !animalId) throw new Error("usuarioId, animalId son requeridos");

    const { data, error } = await supabase
      .from('eventos_reproduccion')
      .insert([{
        id: newId(),
        ...evento,
        animal_id: animalId,
        usuario_id: usuarioId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(eventoId) {
    const { error } = await supabase
      .from('eventos_reproduccion')
      .delete()
      .eq('id', eventoId);

    if (error) throw error;
  },
};

// ─── MOVIMIENTOS (movimientos entre potreros) ──────
export const movimientoService = {
  async getByAnimal(animalId) {
    if (!animalId) throw new Error("animalId es requerido");

    const { data, error } = await supabase
      .from('movimientos')
      .select('*')
      .eq('animal_id', animalId)
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error fetching movimientos:', error);
      return [];
    }
    return data || [];
  },

  async create(usuarioId, animalId, movimiento) {
    if (!usuarioId || !animalId) throw new Error("usuarioId, animalId son requeridos");

    // La UI manda desdeId/hastaId/desdeNombre/hastaNombre (no son columnas).
    // Mapeamos a las columnas reales potrero_desde_id / potrero_hasta_id.
    const m = movimiento || {};
    const { data, error } = await supabase
      .from('movimientos')
      .insert([{
        id: newId(),
        animal_id: animalId,
        usuario_id: usuarioId,
        potrero_desde_id: m.potrero_desde_id ?? m.desdeId ?? null,
        potrero_hasta_id: m.potrero_hasta_id ?? m.hastaId ?? null,
        fecha: m.fecha || null,
        obs: m.obs ?? null
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Todos los movimientos del campo (join con animales) para hidratar el rodeo
  async getByCampo(usuarioId, campoId) {
    if (!usuarioId || !campoId) return [];
    const { data, error } = await supabase
      .from('movimientos')
      .select('id, potrero_desde_id, potrero_hasta_id, fecha, obs, animal_id, animales!inner(campo_id, user_id)')
      .eq('animales.user_id', usuarioId)
      .eq('animales.campo_id', campoId)
      .order('fecha', { ascending: false });
    if (error) { console.error('Error movimientos campo:', error); return []; }
    // La UI lee desdeId/hastaId (así los arma al mover). Devolvemos esos nombres
    // para que el historial funcione igual venga de memoria o de la BD; los
    // *Nombre no son columnas y los resuelve quien tenga la lista de potreros.
    return (data || []).map(m => ({ id: m.id, desdeId: m.potrero_desde_id,
      hastaId: m.potrero_hasta_id, fecha: m.fecha, obs: m.obs, animal_id: m.animal_id }));
  },

  async delete(movimientoId) {
    const { error } = await supabase
      .from('movimientos')
      .delete()
      .eq('id', movimientoId);

    if (error) throw error;
  },
};

// ─── TRATAMIENTOS (tratamientos sanitarios) ────────
export const tratamientoService = {
  async getByAnimal(animalId) {
    if (!animalId) throw new Error("animalId es requerido");

    const { data, error } = await supabase
      .from('tratamientos')
      .select('*')
      .eq('animal_id', animalId)
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error fetching tratamientos:', error);
      return [];
    }
    return data || [];
  },

  async create(usuarioId, campoId, animalId, tratamiento) {
    if (!usuarioId || !animalId) throw new Error("usuarioId, animalId son requeridos");

    // La UI arma un objeto más rico (productoId, total, id...) del que la tabla
    // guarda. Insertamos solo las columnas reales; `tipo` y `unidad` sí se
    // persisten porque Sanidad y la ficha del animal los muestran.
    const { tipo, producto, dosis, unidad, fecha, obs } = tratamiento;
    const { data, error } = await supabase
      .from('tratamientos')
      .insert([{
        id: newId(),
        tipo: tipo ?? null,
        producto: producto ?? null,
        dosis: (dosis === '' || dosis == null) ? null : Number(dosis),
        unidad: unidad ?? null,
        fecha: fecha || null,
        obs: obs ?? null,
        animal_id: animalId,
        usuario_id: usuarioId,
        campo_id: campoId ?? null
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Todos los tratamientos del campo (join con animales) para hidratar el rodeo
  async getByCampo(usuarioId, campoId) {
    if (!usuarioId || !campoId) return [];
    const { data, error } = await supabase
      .from('tratamientos')
      .select('id, tipo, producto, dosis, unidad, fecha, obs, animal_id, animales!inner(campo_id, user_id)')
      .eq('animales.user_id', usuarioId)
      .eq('animales.campo_id', campoId)
      .order('fecha', { ascending: false });
    if (error) { console.error('Error tratamientos campo:', error); return []; }
    return (data || []).map(t => ({ id: t.id, tipo: t.tipo, producto: t.producto, dosis: t.dosis,
      unidad: t.unidad, fecha: t.fecha, obs: t.obs, animal_id: t.animal_id }));
  },

  async delete(tratamientoId) {
    const { error } = await supabase
      .from('tratamientos')
      .delete()
      .eq('id', tratamientoId);

    if (error) throw error;
  },
};

// ─── DISTRIBUCIONES_FARDOS (distribución de fardos) ─
export const distribucionFardoService = {
  async getByFardo(fardoId) {
    if (!fardoId) throw new Error("fardoId es requerido");

    const { data, error } = await supabase
      .from('distribuciones_fardos')
      .select('*')
      .eq('fardo_id', fardoId)
      .order('fecha', { ascending: false });

    if (error) {
      console.error('Error fetching distribuciones_fardos:', error);
      return [];
    }
    return data || [];
  },

  async create(fardoId, distribucion) {
    if (!fardoId) throw new Error("fardoId es requerido");

    const { data, error } = await supabase
      .from('distribuciones_fardos')
      .insert([{
        id: newId(),
        ...distribucion,
        fardo_id: fardoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(distribucionId) {
    const { error } = await supabase
      .from('distribuciones_fardos')
      .delete()
      .eq('id', distribucionId);

    if (error) throw error;
  },
};

// ─── TAREAS (tareas/agenda) ──────────────────────────
// Las FK opcionales llegan como "" desde los <select>; hay que mandarlas como
// null o Postgres rechaza el insert (violación de foreign key con id vacío).
const TAREA_FKS = ['potrero_id', 'zona_id', 'maquina_id', 'empleado_id'];
function limpiarTarea(tarea) {
  const out = { ...tarea };
  for (const k of TAREA_FKS) {
    if (out[k] === '') out[k] = null;   // solo convertir los presentes vacíos
  }
  return out;
}

export const tareaService = {
  async getAll(usuarioId, campoId) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('tareas')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .order('fecha', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, campoId, tarea) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('tareas')
      .insert([{
        id: newId(),
        ...limpiarTarea(tarea),
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(tareaId, updates) {
    const { data, error } = await supabase
      .from('tareas')
      .update(limpiarTarea(updates))
      .eq('id', tareaId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(tareaId) {
    const { error } = await supabase
      .from('tareas')
      .delete()
      .eq('id', tareaId);

    if (error) throw error;
  },
};

// ─── EMPLEADOS ───────────────────────────────────────
export const empleadoService = {
  async getAll(usuarioId, campoId) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('empleados')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .eq('activo', true)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, campoId, empleado) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('empleados')
      .insert([{
        id: newId(),
        ...empleado,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(empleadoId, updates) {
    const { data, error } = await supabase
      .from('empleados')
      .update(updates)
      .eq('id', empleadoId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(empleadoId) {
    const { error } = await supabase
      .from('empleados')
      .update({ activo: false })
      .eq('id', empleadoId);

    if (error) throw error;
  },
};

// ─── MAQUINARIAS ─────────────────────────────────────
export const maquinariaService = {
  async getAll(usuarioId, campoId) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('maquinarias')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, campoId, maquina) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('maquinarias')
      .insert([{
        id: newId(),
        ...maquina,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(maquinaId, updates) {
    const { data, error } = await supabase
      .from('maquinarias')
      .update(updates)
      .eq('id', maquinaId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(maquinaId) {
    const { error } = await supabase
      .from('maquinarias')
      .delete()
      .eq('id', maquinaId);

    if (error) throw error;
  },
};

// ─── MANTENIMIENTOS ──────────────────────────────────
export const mantenimientoService = {
  async getByMaquina(maquinaId) {
    if (!maquinaId) throw new Error("maquinaId es requerido");

    const { data, error } = await supabase
      .from('mantenimientos')
      .select('*')
      .eq('maquina_id', maquinaId)
      .order('fecha', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, maquinaId, mantenimiento) {
    if (!usuarioId || !maquinaId) throw new Error("usuarioId y maquinaId son requeridos");

    const { data, error } = await supabase
      .from('mantenimientos')
      .insert([{
        id: newId(),
        ...mantenimiento,
        user_id: usuarioId,
        maquina_id: maquinaId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(mantenimientoId) {
    const { error } = await supabase
      .from('mantenimientos')
      .delete()
      .eq('id', mantenimientoId);

    if (error) throw error;
  },
};

// ─── ZONAS ───────────────────────────────────────────
export const zonaService = {
  async getAll(usuarioId, campoId) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('zonas')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, campoId, zona) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('zonas')
      .insert([{
        id: newId(),
        ...zona,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(zonaId, updates) {
    const { data, error } = await supabase
      .from('zonas')
      .update(updates)
      .eq('id', zonaId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(zonaId) {
    const { error } = await supabase
      .from('zonas')
      .delete()
      .eq('id', zonaId);

    if (error) throw error;
  },
};

// ─── INSUMOS ──────────────────────────────────────────
export const insumoService = {
  async getAll(usuarioId, campoId) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('insumos')
      .select('*')
      .eq('user_id', usuarioId)
      .eq('campo_id', campoId)
      .order('nombre', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, campoId, insumo) {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId son requeridos");

    const { data, error } = await supabase
      .from('insumos')
      .insert([{
        id: newId(),
        ...insumo,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(insumoId, updates) {
    const { data, error } = await supabase
      .from('insumos')
      .update(updates)
      .eq('id', insumoId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(insumoId) {
    const { error } = await supabase
      .from('insumos')
      .delete()
      .eq('id', insumoId);

    if (error) throw error;
  },
};

// ─── MOVIMIENTOS_INSUMOS ──────────────────────────────
export const movimientoInsumoService = {
  async getByInsumo(insumoId) {
    if (!insumoId) throw new Error("insumoId es requerido");

    const { data, error } = await supabase
      .from('movimientos_insumos')
      .select('*')
      .eq('insumo_id', insumoId)
      .order('fecha', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, insumoId, movimiento) {
    if (!usuarioId || !insumoId) throw new Error("usuarioId y insumoId son requeridos");

    const { data, error } = await supabase
      .from('movimientos_insumos')
      .insert([{
        id: newId(),
        ...movimiento,
        user_id: usuarioId,
        insumo_id: insumoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(movimientoId) {
    const { error } = await supabase
      .from('movimientos_insumos')
      .delete()
      .eq('id', movimientoId);

    if (error) throw error;
  },
};

// ─── ZONAS_PRODUCCION ────────────────────────────────
export const zonasProduccionService = {
  async getByZona(zonaId) {
    if (!zonaId) throw new Error("zonaId es requerido");

    const { data, error } = await supabase
      .from('zonas_produccion')
      .select('*')
      .eq('zona_id', zonaId)
      .order('fecha', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, campoId, zonaId, produccion) {
    if (!usuarioId || !campoId || !zonaId) throw new Error("usuarioId, campoId, zonaId son requeridos");

    const { data, error } = await supabase
      .from('zonas_produccion')
      .insert([{
        id: newId(),
        ...produccion,
        zona_id: zonaId,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(produccionId) {
    const { error } = await supabase
      .from('zonas_produccion')
      .delete()
      .eq('id', produccionId);

    if (error) throw error;
  },
};

// ─── ZONAS_RACION ────────────────────────────────────
export const zonasRacionService = {
  async getByZona(zonaId) {
    if (!zonaId) throw new Error("zonaId es requerido");

    const { data, error } = await supabase
      .from('zonas_racion')
      .select('*')
      .eq('zona_id', zonaId)
      .order('fecha', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, campoId, zonaId, racion) {
    if (!usuarioId || !campoId || !zonaId) throw new Error("usuarioId, campoId, zonaId son requeridos");

    const { data, error } = await supabase
      .from('zonas_racion')
      .insert([{
        id: newId(),
        ...racion,
        insumo_id: racion.insumo_id || null,   // "" viola la FK a insumos
        zona_id: zonaId,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(racionId) {
    const { error } = await supabase
      .from('zonas_racion')
      .delete()
      .eq('id', racionId);

    if (error) throw error;
  },
};

// ─── ZONAS_VENTAS ────────────────────────────────────
export const zonasVentasService = {
  async getByZona(zonaId) {
    if (!zonaId) throw new Error("zonaId es requerido");

    const { data, error } = await supabase
      .from('zonas_ventas')
      .select('*')
      .eq('zona_id', zonaId)
      .order('fecha', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, campoId, zonaId, venta) {
    if (!usuarioId || !campoId || !zonaId) throw new Error("usuarioId, campoId, zonaId son requeridos");

    const { data, error } = await supabase
      .from('zonas_ventas')
      .insert([{
        id: newId(),
        ...venta,
        zona_id: zonaId,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(ventaId) {
    const { error } = await supabase
      .from('zonas_ventas')
      .delete()
      .eq('id', ventaId);

    if (error) throw error;
  },
};

// ─── ZONAS_MOVIMIENTOS (ingreso/egreso de animales en la zona) ─────
export const zonasMovimientoService = {
  async getByZona(zonaId) {
    if (!zonaId) throw new Error("zonaId es requerido");

    const { data, error } = await supabase
      .from('zonas_movimientos')
      .select('*')
      .eq('zona_id', zonaId)
      .order('fecha', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async create(usuarioId, campoId, zonaId, mov) {
    if (!usuarioId || !campoId || !zonaId) throw new Error("usuarioId, campoId, zonaId son requeridos");

    const { data, error } = await supabase
      .from('zonas_movimientos')
      .insert([{
        id: newId(),
        ...mov,
        zona_id: zonaId,
        user_id: usuarioId,
        campo_id: campoId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(movId) {
    const { error } = await supabase
      .from('zonas_movimientos')
      .delete()
      .eq('id', movId);

    if (error) throw error;
  },
};

// ─── TIENDA: PROVEEDORES + PRODUCTOS (directorio global) ──────────
export const proveedorService = {
  async getAll() {
    const { data, error } = await supabase
      .from('proveedores')
      .select('id, nombre, categoria, localidad, descripcion, telefono, web, proveedores_productos(nombre, precio, unidad)')
      .order('nombre', { ascending: true });

    if (error) {
      console.error('Error cargando proveedores:', error);
      return [];
    }
    // Mapear a la forma que usa el componente
    return (data || []).map(p => ({
      id: p.id,
      nombre: p.nombre,
      cat: p.categoria,
      localidad: p.localidad,
      desc: p.descripcion,
      tel: p.telefono,
      web: p.web,
      productos: p.proveedores_productos || [],
    }));
  },
};
